import * as React from "react";
import {cx} from "emotion";

import "./logos-panel.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "@Common/react/load2";
import {ConfirmModal}   from "@Common/ui-components/confirm-modal/confirm-modal";
import {ItemGrid}       from "@Common/ui-components/heavy/mailing-ui/create-mailing-list-modal/select-type/item-grid";

import {EmulationModal}                           from "../../../common/emulation-modal/emulation-modal";
import {AGENT_LEVEL, BRANCH_LEVEL, COMPANY_LEVEL} from "../../../common/logic/user-level";
import {BlobToFile}                               from "../blob-to-file";
import {ImageAssetEditModal}                      from "../image-asset-edit-modal/image-asset-edit-modal";
import {UploadCard}                               from "../upload-card/upload-card";

import {LogoCard} from "./logos-card/logos-card";

export const AccountLogosPanel = ({agentId, onReloadLogo}:{agentId?: string, onReloadLogo?: () => void}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("userLevels"),
    ["emulationModal", (_, next) => EmulationModal({next})],
    ["confirmModal", (_, next) => ConfirmModal({next})],
    ["agent", ({apis}, next) => Load2({
            fetch: () => apis.next.session.getAgent({agentId}),
            next,
        })
    ],
    ["logos", ({agent}, next) =>  next(agent.value?.imageGallery?.logos)],
    ["userCanEdit", ({company, userLevels}, next) => userLevels.userLevel === AGENT_LEVEL ? (
        next(company?.prefs?.agentCanEditLogos)
    ) : (
        userLevels.userLevel === BRANCH_LEVEL ? (
            next(company?.prefs?.officeCanEditLogos)
    ) : (
        next(true)
    ))],
    ({agent, logos, userCanEdit}, next) => (
        <div className="logos-panel-w9n">
            {!agent.loading && !userCanEdit && (
                <div className={cx({center: logos?.length === 0, info: logos?.length !== 0})}>
                    You do not have the ability to modify logos. Please see your admin for more information.
                </div>
            )}
            {next()}
        </div>
    ),
    ({logos, agent, userLevels, emulationModal, userCanEdit, confirmModal}) => (
        agent.loading ? (
            <div className="loading" xd-qa-type="label" xd-qa-name="Loading">
                Loading...
            </div>
        ) : (!agentId && (!agent?.idAgent || agent?.idAgent === "Branch Order")) ? (
            <div className="center">
                {userLevels.userLevel !== AGENT_LEVEL ? (
                    <>
                        {"In order to manage user logos, you must "}
                        <a
                            className="emulation"
                            xd-qa-type="button"
                            xd-qa-name="Emulation"
                            onClick={() => emulationModal.show()}
                        >
                            {userLevels.userLevel === COMPANY_LEVEL ? (
                                "Select Office & User"
                            ) : (
                                "Select User"
                            )}
                        </a>
                    </>
                ) : (
                    "Something went wrong"
                )}
            </div>
        ) : cs(
            consumeContext("apis"),
            ["reload", (_, next) => next(() => Promise.all([
                agent.reload(),
                onReloadLogo?.(),
            ]))],
            ["editModal", ({apis, reload}, next) => userCanEdit ? ImageAssetEditModal({
                agentId,
                onRemoveBackground: () => reload(),
                onEdit: async ({id, data}) => {
                    await apis.next.gallery.logos.update({
                        id,
                        postData: data,
                        agentId,
                    });
                    reload();
                },
                onImageEdit: async ({mimeString, name: suggestedName, content}) => {

                    const name = (() => {
                        for (let i = 0; ; i++) {
                            const proposedName = suggestedName + (i === 0 ? "" : `(${i})`);
                            if (!logos?.some((logo) => logo.name === proposedName)) {
                                return proposedName;
                            }
                        }
                    })();

                    const result = await apis.next.gallery.logos.upload({
                    formData: {
                        file: BlobToFile({
                            name: name + ".png",
                            data: content,
                            mimeString,
                        }),
                        idEntity: agentId,
                        name,
                    },
                    });
                    if (result.errorMessage) {
                        alert(result.errorMessage);
                    } else {
                        reload();
                    }
                },
                onDelete: async (modal) => {
                    confirmModal.show({
                        messageLines: ["Are you sure you want to delete this logo?"],
                        confirm: async () => {
                            await apis.next.gallery.logos.delete(modal.data.id, {agentId});
                            await reload();
                            modal.close();
                        }
                    })
                },
                next,
            }) : next()],
            ["logoCards", ({apis, editModal, reload}, next) => next((logos ?? []).map(logo => LogoCard({
                logo,
                ...userCanEdit && {
                    onEdit: () => editModal.show({data: logo, type: "Logo"}),
                    onDelete: async () => {
                        confirmModal.show({
                            messageLines: ["Are you sure you want to delete this logo?"],
                            confirm: async () => {
                                await apis.next.gallery.logos.delete(logo.id, {agentId});
                                await reload();
                            }
                        })
                    },
                },
            })))],
            ["uploadCard", ({apis, reload}, next) => next(userCanEdit ? (
                UploadCard({
                    type: "Logo",
                    onUpload: async (data) => {
                        const result = await apis.next.gallery.logos.upload({
                            formData: {file: data.file, name: data.name, idEntity: agentId},
                        });
                        if (result.errorMessage) {
                            alert(result.errorMessage);
                            return false;
                        } else {
                            reload();
                            return true;
                        }
                    },
                })
            ) : (
                null
            ))],

            ["itemGrid", ({}, next) => ItemGrid({
                minWidth: 325,
                maxWidth: 450,
                minGap: 10,
                maxGap: 20,
                count: (logos?.length ?? 0) + userCanEdit,
                stretch: true,
                next,
            })],
            ["marginRight", ({itemGrid}, next) => next(index => (
                (
                    (index % itemGrid.colCount === itemGrid.colCount - 1) ||
                    (index === (logos ? logos.length + userCanEdit - 1 : 0))
                ) ? undefined : itemGrid.gapSize
            ))],
            ({itemGrid, uploadCard, marginRight, logoCards}) => (
                <div className="content" ref={itemGrid.containerRef}>
                    {[
                        uploadCard,
                        ...logoCards,
                    ].filter(card => card).map((card, i) => (
                        <div
                            className="card"
                            style={{...itemGrid.cellWidth && {
                                width: (logos?.length ?? 0) + userCanEdit > 1 ? (
                                    Math.floor(itemGrid.cellWidth)
                                ) : (
                                    Math.min(Math.floor(itemGrid.cellWidth), 450)
                                )
                            }, marginRight: marginRight(i)}}
                            key={i}
                        >
                            {card.render()}
                        </div>
                    ))}
                </div>
            )
        )
    ),
);
